import { cva } from 'class-variance-authority'
import { XIcon } from 'lucide-react'
import React, { forwardRef, useMemo } from 'react'

import { type IconProp } from 'slab/types'

export interface BadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'className'> {
  /**
   * The icon to render when the badge is actionable (i.e. the `onActionClick`
   * prop is provided). Defaults to an "x" icon.
   *
   * If you provide an icon other than the default "x" icon, you must also provide
   * an `actionLabel` to accurately identify the action to screen readers.
   */
  actionIcon?: IconProp;
  /**
   * Defines the accessible name for the action button, which is required for
   * screen readers since the button contains no visible text. Defaults to
   * "dismiss."
   *
   * If you use the `actionIcon` prop to customize the action, you must also
   * provide an explicit label to accurately identify the action to screen readers.
   */
  actionLabel?: string;
  bordered?: boolean;
  children: React.ReactNode;
  leftIcon?: IconProp;
  /**
   * When provided, an action button will be rendered on the right side of the badge.
   *
   * By default the button will render an "x" icon and will be labeled "dismiss"
   * for screen readers. Use the `actionIcon` and `actionLabel` props to
   * customize the icon and accurately identify the action to screen readers.
   */
  onActionClick?: () => void;
  type?: 'info' | 'success' | 'caution' | 'warning' | 'critical' | 'neutral' | 'slate' | 'thatch';
}

const badgeClassNames = cva(
  'inline-flex items-center rounded-lg body-sm-bold',
  {
    variants: {
      bordered: {
        true: 'border',
        false: null,
      },
      hasAction: {
        true: 'pl-2',
        false: 'px-2',
      },
      type: {
        info: 'bg-blue-50 text-blue-500',
        success: 'bg-green-50 text-green-500',
        caution: 'bg-yellow-50 text-yellow-500',
        warning: 'bg-orange-50 text-orange-500',
        critical: 'bg-red-50 text-red-700',
        neutral: 'bg-gray-100 text-gray-800',
        slate: 'bg-slate-50 text-slate-500',
        thatch: 'bg-thatch-100 text-thatch-600',
      },
    },
    compoundVariants: [
      {
        bordered: true,
        type: 'info',
        className: 'border-blue-300',
      },
      {
        bordered: true,
        type: 'success',
        className: 'border-green-300',
      },
      {
        bordered: true,
        type: 'caution',
        className: 'border-yellow-300',
      },
      {
        bordered: true,
        type: 'warning',
        className: 'border-orange-300',
      },
      {
        bordered: true,
        type: 'critical',
        className: 'border-red-300',
      },
      {
        bordered: true,
        type: 'neutral',
        className: 'border-gray-400',
      },
      {
        bordered: true,
        type: 'slate',
        className: 'border-slate-300',
      },
      {
        bordered: true,
        type: 'thatch',
        className: 'border-thatch-300',
      },
    ],
  }
)

const iconClassNames = cva('flex items-center h-3', {
  variants: {
    type: {
      info: 'text-blue-400',
      success: 'text-green-400',
      caution: 'text-yellow-400',
      warning: 'text-orange-400',
      critical: 'text-red-400',
      neutral: 'text-gray-500',
      slate: 'text-slate-400',
      thatch: 'text-thatch-600',
    }
  }
})

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(({
  actionIcon: ActionIcon = XIcon,
  actionLabel = 'dismiss',
  bordered = false,
  children,
  leftIcon: LeftIcon,
  onActionClick,
  type = 'neutral',
  ...props
}, forwardedRef) => {
  const hasAction = !!onActionClick

  const badgeClassName = useMemo(
    () => badgeClassNames({ bordered, hasAction, type }),
    [hasAction, type]
  )
  const iconClassname = useMemo(() => iconClassNames({ type }), [type])

  return (
    <span {...props} className={badgeClassName} ref={forwardedRef}>
      <span className='inline-flex items-center gap-1'>
        {!!LeftIcon && (
          <span className={iconClassname}>
            <LeftIcon aria-hidden size={12} />
          </span>
        )}
        <span className='self-baseline py-1 text-nowrap'>{children}</span>
      </span>
      {hasAction && (
        <button
          aria-label={actionLabel}
          className='appearance-none rounded-md transition-all h-6 w-6 flex items-center justify-center text-black/50 hover:text-black/80 outline outline-2 outline-transparent focus-visible:outline-thatch-500'
          name='action'
          onClick={onActionClick}
        >
          <ActionIcon aria-hidden size={12} />
        </button>
      )}
    </span>
  )
})
